import { keyBy } from 'lodash';
import {
  useMutation,
  UseMutationResult,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
  useQueryClient,
} from 'react-query';
import { ProjectAccountingAPI } from 'api/settings-financials';
import type {
  CustomRevenueRecognitionConfigApi,
  FinancialNotificationConfig,
  RevenueRecognitionConfig,
  RevenueRecognitionConfigApi,
  UpdateFinancialAlertPayload,
} from 'api/settings-financials/types';
import { sortByText } from 'utils/sort-by-text';

export const FinancialNotificationsConfigQueryKey = (): [string] => [
  'financial-notifications-key',
];

export const useFinancialNotificationsConfigQuery = (
  options: UseQueryOptions<
    FinancialNotificationConfig[],
    unknown,
    FinancialNotificationConfig[],
    [string]
  > = {},
): UseQueryResult<FinancialNotificationConfig[]> => {
  return useQuery(
    FinancialNotificationsConfigQueryKey(),
    () => ProjectAccountingAPI.getFinancialNotificationConfig(),
    {
      ...options,
    },
  );
};

export const useFinancialNotificationsConfigMutation = (
  configId: number,
  options: UseMutationOptions<
    FinancialNotificationConfig[],
    unknown,
    UpdateFinancialAlertPayload | FinancialNotificationConfig
  > = {},
): UseMutationResult<
  FinancialNotificationConfig[],
  unknown,
  UpdateFinancialAlertPayload | FinancialNotificationConfig
> => {
  const queryClient = useQueryClient();

  return useMutation(
    (payload) =>
      ProjectAccountingAPI.updateFinancialNotificationConfig(configId, payload),
    {
      onSuccess: () => {
        queryClient.refetchQueries(FinancialNotificationsConfigQueryKey());
      },
      ...options,
    },
  );
};

export const RevenueRecognitionConfigQueryKey = (): [string] => [
  'revenue-recoginition-query-key',
];

const getTransformedRevenueRecognitionConfig = (
  config: RevenueRecognitionConfigApi[],
): RevenueRecognitionConfig =>
  keyBy(config, 'financialContractType') as RevenueRecognitionConfig;

export const useRevenueRecognitionConfigQuery = (
  options: UseQueryOptions<
    RevenueRecognitionConfig,
    unknown,
    RevenueRecognitionConfig,
    [string]
  > = {},
): UseQueryResult<RevenueRecognitionConfig> => {
  return useQuery(
    RevenueRecognitionConfigQueryKey(),
    async () => {
      const config = await ProjectAccountingAPI.getRevenueRecognitionConfig();

      return getTransformedRevenueRecognitionConfig(config);
    },
    {
      ...options,
    },
  );
};

export const useRevenueRecognitionConfigMutation = (
  options: UseMutationOptions<
    void,
    unknown,
    RevenueRecognitionConfigApi,
    RevenueRecognitionConfig
  > = {},
): UseMutationResult<void, unknown, RevenueRecognitionConfigApi> => {
  return useMutation(
    (payload) => ProjectAccountingAPI.updateRevenueRecognitionConfig(payload),
    {
      ...options,
    },
  );
};

const CUSTOM_REVENUE_RECOGNITION_CONFIG_QUERY_KEY =
  'custom-revenue-recognition-query';

export const customRevenueRecognitionQueryKey = (includeProjects = false) => [
  CUSTOM_REVENUE_RECOGNITION_CONFIG_QUERY_KEY,
  includeProjects.valueOf().toString(),
];

export const useCustomRevenueRecognitionQuery = (
  includeProjects = false,
  options: UseQueryOptions<
    CustomRevenueRecognitionConfigApi[],
    unknown,
    CustomRevenueRecognitionConfigApi[],
    string[]
  > = {},
) => {
  return useQuery(
    customRevenueRecognitionQueryKey(includeProjects),
    () =>
      ProjectAccountingAPI.getCustomRevenueRecognitionConfig(includeProjects),
    {
      select: (data) => data?.sort((a, b) => sortByText(a.name, b.name)),
      ...options,
    },
  );
};

export const useCreateCustomRevenueRecognitionMutation = (
  options: UseMutationOptions<
    void,
    unknown,
    CustomRevenueRecognitionConfigApi,
    unknown
  > = {},
) => {
  const queryClient = useQueryClient();

  return useMutation(
    (payload) =>
      ProjectAccountingAPI.createCustomRevenueRecognitionConfig(payload),
    {
      ...options,
      onSuccess: async () => {
        await queryClient.refetchQueries(
          CUSTOM_REVENUE_RECOGNITION_CONFIG_QUERY_KEY,
        );
      },
    },
  );
};

export const useUpdateCustomRevenueRecognitionMutation = (
  customRevenueRecognitionId: number,
  options: UseMutationOptions<
    void,
    unknown,
    CustomRevenueRecognitionConfigApi,
    unknown
  > = {},
) => {
  const queryClient = useQueryClient();

  return useMutation(
    (payload) =>
      ProjectAccountingAPI.updateCustomRevenueRecognitionConfig(
        customRevenueRecognitionId,
        payload,
      ),
    {
      ...options,
      onSuccess: async () => {
        await queryClient.refetchQueries(
          CUSTOM_REVENUE_RECOGNITION_CONFIG_QUERY_KEY,
        );
      },
    },
  );
};

export const useDeleteCustomRevenueRecognitionMutation = (
  customRevenueRecognitionConfigId: number,
  options: UseMutationOptions<void, unknown, void, unknown> = {},
) => {
  const queryClient = useQueryClient();

  return useMutation(
    () =>
      ProjectAccountingAPI.deleteCustomRevenueRecognitionConfig(
        customRevenueRecognitionConfigId,
      ),
    {
      ...options,
      onSuccess: async () => {
        await queryClient.refetchQueries(
          CUSTOM_REVENUE_RECOGNITION_CONFIG_QUERY_KEY,
        );
      },
    },
  );
};
